import { isNonEmpty } from '@sgme/fp';
import { parseISO } from 'date-fns';
import { AllowedRequests } from 'models/forms/FormResult';
import type { Underlying } from 'models/underlying';
import type { DataById } from 'state/formData';
import { formEnrichedDataReceived } from 'state/formData/formData.action';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { commonTradeAction } from 'state/forms/actions/trade';
import { HandledWorkspace } from 'state/forms/models';
import { checkMonthlyExpiryCalendarsRequestNeededThunk } from 'state/monthlyExpiryDates/monthlyExpiryDates.thunks';
import {
  costAndChargeReloaded,
  kidDocumentReloaded,
  productGovernanceAssessmentReloaded,
  productGovernanceDocumentReloaded,
  tradeAuthorizationReceived,
  tradeAuthorizationTimeout,
} from 'state/quotes/regulations/regulation.actions';
import { QuoteWithRegulation } from 'state/quotes/results/result.model';
import { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import {
  getFormEnrichedBackedNotional,
  getFormEnrichedBackToBackFileCode,
  getFormEnrichedBackToBackType,
  getFormEnrichedIsBackToBack,
  getFormEnrichedIsDelayedBackToBack,
  getFormEnrichedIsDeltaAdjusted,
  getFormEnrichedNoteNominal,
  getIsUserInternal,
} from 'state/selectors';
import { checkSettlementCalendarsUpdateRequestNeededThunk } from 'state/settlementDateCalendars/settlementDateCalendars.thunks';
import type { Thunk, ThunkDispatch } from 'state/state.common';
import { tradeConfirmationInitBackToBack } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import { checkValuationDateCalendarsRequestNeededThunk } from 'state/valuationDateCalendars/valuationDateCalendars.thunks';
import type { WorkspaceType } from 'state/workspaces/model';
import { isForexUnderlying } from 'utils/underlying/typeGuards';

export const dispatchCommonFormActionsForWorkspace =
  (workspaceType: WorkspaceType) =>
  (dispatch: ThunkDispatch, tabId: string, values: DataById, allowedRequests?: AllowedRequests, formId?: string) => {
    dispatch(formEnrichedDataReceived(tabId, formId ?? tabId, values, workspaceType));

    if (allowedRequests?.requestValuationCalendar) {
      dispatch(checkValuationDateCalendarsRequestNeededThunk(values.ValuationDatesCalendar as string[]));
    }
    const underlyings = values.Underlyings as Underlying[];
    if (allowedRequests?.requestSettlementCalendar) {
      const underlyingCurrencies = new Set(
        (underlyings ?? []).filter(isForexUnderlying).flatMap(u => [u.currency1, u.currency2]),
      );
      underlyingCurrencies.add(values.Currency as string);
      underlyingCurrencies.forEach(ccy => dispatch(checkSettlementCalendarsUpdateRequestNeededThunk(ccy)));
    }

    if (allowedRequests?.requestMonthlyExpirationDate) {
      const underlyingCalendarCodes = [
        ...new Set(
          (underlyings ?? [])
            .filter(u => isNonEmpty(u.calendar?.id))
            .map(u => u.calendar!.id)
            .filter(isNonEmpty),
        ),
      ];
      dispatch(checkMonthlyExpiryCalendarsRequestNeededThunk(underlyingCalendarCodes));
    }
  };

export function statifiedPreviousQuoteLoadedThunk(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  quoteWithRegulation: QuoteWithRegulation,
): Thunk<void> {
  return dispatch => {
    dispatch(commonQuoteAction.previousQuoteLoaded(workspace, tabId, quoteId, quoteWithRegulation.quote));

    if (quoteWithRegulation.costAndCharge !== undefined) {
      dispatch(costAndChargeReloaded(workspace, tabId, quoteId, quoteWithRegulation.costAndCharge));
    }

    if (quoteWithRegulation.kidDocumentation !== undefined) {
      dispatch(kidDocumentReloaded(workspace, tabId, quoteId, quoteWithRegulation.kidDocumentation));
    }

    if (quoteWithRegulation.productGovernanceDocumentation !== undefined) {
      dispatch(
        productGovernanceDocumentReloaded(
          workspace,
          tabId,
          quoteId,
          quoteWithRegulation.productGovernanceDocumentation,
        ),
      );
    }

    if (quoteWithRegulation.productGovernanceAssessment !== undefined) {
      dispatch(
        productGovernanceAssessmentReloaded(workspace, tabId, quoteId, quoteWithRegulation.productGovernanceAssessment),
      );
    }
  };
}

export function statifiedTradeAuthReceivedThunk(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
  tradeAuth: NextTradeAuthorizationResult,
): Thunk<void> {
  return (dispatch, _getState, { getNow }) => {
    const tradeAuthCreatedOn = tradeAuth.createdOn;

    const when =
      tradeAuth.status === 'Granted' && isNonEmpty(tradeAuthCreatedOn) ? parseISO(tradeAuthCreatedOn) : getNow();

    dispatch(commonTradeAction.tradeAuthReceived(workspace, tabId, quoteId, tradeAuth, when));
    dispatch(tradeAuthorizationReceivedWithBackToBackInitThunk(workspace, tabId, quoteId));
  };
}

export function tradeAuthorizationReceivedWithBackToBackInitThunk(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(tradeAuthorizationReceived(workspace, tabId, quoteId));

    const state = getState();
    if (!getIsUserInternal(state)) {
      return;
    }

    const isBackToBack = getFormEnrichedIsBackToBack(state, tabId);

    if (!isBackToBack) {
      return;
    }

    const fileCode = getFormEnrichedBackToBackFileCode(state, tabId);
    const noteNominal = getFormEnrichedNoteNominal(state, tabId);
    const isDeltaAdjusted = getFormEnrichedIsDeltaAdjusted(state, tabId);
    const backToBackType = getFormEnrichedBackToBackType(state, tabId);
    const isDelayedBackToBack = getFormEnrichedIsDelayedBackToBack(state, tabId);
    const backedNotional = getFormEnrichedBackedNotional(state, tabId);

    if (backToBackType !== 'ClientTrade' && backToBackType !== 'HedgingTrade') {
      return;
    }

    dispatch(
      tradeConfirmationInitBackToBack(
        quoteId,
        isBackToBack,
        fileCode,
        noteNominal,
        isDeltaAdjusted,
        backToBackType,
        isDelayedBackToBack,
        backedNotional,
      ),
    );
  };
}

export function statifiedTradeAuthTimeoutThunk(
  workspace: HandledWorkspace,
  tabId: string,
  quoteId: string,
): Thunk<void> {
  return dispatch => {
    dispatch(commonTradeAction.tradeAuthTimeout(workspace, tabId, quoteId));
    dispatch(tradeAuthorizationTimeout(tabId, quoteId));
  };
}
